import RoutePackageSet from './multiRoute/RoutePackageSet';
import { type RoutePackageLoaderConfig } from './multiRoute/types';
import ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPackageLoader from '@itinerary/trip/TripPackageLoader';
import { type TripPackageLoaderResult } from '@itinerary/trip/TripPackageLoader';
import {
    LoadingStatus,
    type FilterData,
    type FilterKey,
} from '@itinerary/ItineraryModuleTypes';
import { type Point, TripSortBy } from '@itinerary/trip/TripModuleTypes';
import { type TripPackageLoaderConfig } from '@itinerary/trip/types';

/**
 * Stores data related to a list of packages, that one of them must be chosen
 */
export default abstract class TripPackageSet<
    TFilterMetaData extends FilterData = FilterData,
    TFilterKey extends FilterKey = FilterKey,
    TPackage extends TripPackage = TripPackage,
> extends ItineraryPackageSet<TFilterMetaData, TFilterKey, TPackage> {
    /**
     * The package loader associated with this set
     */
    protected abstract override loader: TripPackageLoader<TPackage>;

    private routePackageSet: RoutePackageSet | undefined;

    /**
     * The coordinates of the origin location, that is loaded along with the list of packages
     */
    originLocation: Point | null = null;

    /**
     * The coordinates of the destination location, that is loaded along with the list of packages
     */
    destinationLocation: Point | null = null;
    private isRoundTrip = false;

    /**
     * Stores the current sorting method
     */
    override sortBy: TripSortBy = TripSortBy.Price;

    override reinitialize(
        config: TripPackageLoaderConfig,
        filters: Record<TFilterKey, any>
    ) {
        this.isRoundTrip = config.isRoundTrip;
        super.reinitialize(config, filters);
    }

    override onGenerate(result: TripPackageLoaderResult<TPackage>) {
        super.onGenerate(result);
        this.originLocation = result.originLocation;

        this.destinationLocation = result.destinationLocation;
        // Finding the minimum price of the list
        const minPrice = this.list
            .filter(
                item =>
                    item.defaultPrice?.singlePayable &&
                    item.capacity > 0 &&
                    item.reservable
            )
            .reduce((min: number | null, curr) => {
                if (min === null || min > curr.defaultPrice!.singlePayable) {
                    min = curr.defaultPrice!.singlePayable;
                }
                return min;
            }, null);

        // Setting the minimum price to be shown on the suggestion tab bar

        this.minPrice = minPrice ?? undefined;
    }

    get isRouteEnabled() {
        const posthog = usePostHog();
        const [, cQuery] = this.url?.split('?') ?? [];
        const limit = this.list.filter(pck => pck.reservable).length <= 2;
        const isEnabled =
            posthog.featureFlags?.['multi-route-enabled'] === 'test';
        return limit && !!cQuery && isEnabled && !this.isRoundTrip;
    }

    override async loadAvailable(): Promise<void> {
        const posthog = usePostHog();
        try {
            await super.loadAvailable();
        } finally {
            await posthog.loadPromise;
            if (this.isRouteEnabled) {
                await this.loadAvailableRoutePackages();
            }
        }
    }

    get routePackages() {
        return this.routePackageSet?.list;
    }

    get isLoadingRoutePackages() {
        const loadingStatus = this.routePackageSet?.loadingStatus;
        return (
            loadingStatus === LoadingStatus.Loading ||
            loadingStatus === LoadingStatus.RealtimeLoading
        );
    }

    get originName() {
        return this.loader.originName;
    }

    get destinationName() {
        return this.loader.destinationName;
    }

    get originCode() {
        return this.loader.originCode;
    }

    get destinationCode() {
        return this.loader.destinationCode;
    }

    private generateRouteData(): RoutePackageLoaderConfig {
        return {
            originCode: this.originCode,
            destinationCode: this.destinationCode,
            url: this.loader.url,
            date: this.loader.date,
            adultCount: this.loader.adultCount,
            childCount: this.loader.childCount,
            infantCount: this.loader.infantCount,
            totalSeatCount:
                this.loader.adultCount +
                this.loader.childCount +
                this.loader.infantCount,
            reserveType: this.loader.reserveType,
            isRoundTrip: false,
        };
    }

    private async loadAvailableRoutePackages() {
        this.routePackageSet = new RoutePackageSet(this.generateRouteData());
        await this.routePackageSet.loadAvailable();
    }

    protected override reset(
        config: TripPackageLoaderConfig,
        filters: Record<TFilterKey, any>
    ) {
        super.reset(config, filters);
        this.sortBy = TripSortBy.Price;
        this.originLocation = null;
        this.destinationLocation = null;
        this.routePackageSet?.reset(this.generateRouteData());
    }

    protected constructor(
        /**
         * Contains filters that must be applied to the results
         */
        public override filters: Record<TFilterKey, any>
    ) {
        super(filters);
    }
}
